import {
    ApiGetTemplateList,
    type ApiGetTemplateListResponse,
    type LoadedTemplate,
} from "@/api/ApiGetTemplateList";
import { canv } from "@/canv/global.js";
import { debounce, getTarget, logException } from "@/canv/utils.js";
import type { Group } from "@/group/group";
import { selectionManager } from "@/tools/cursorTool";

export class TemplatesWindow {
    async open() {
        const templatesWindowWrapper = document.createElement("div");
        document.body.append(templatesWindowWrapper);

        templatesWindowWrapper.outerHTML = this.getWindow();

        const contentContainer = document.querySelector(".templatesWindowContent");
        if (contentContainer) {
            this.showLoader();
            const contentData = await this.getContentData();
            this.hideLoader();
            if (this.loadedTemplates) {
                for (let i = 0; i < this.loadedTemplates.length; i++) {
                    contentContainer.innerHTML += this.applyItemTemplate(contentData[i]);
                }
            }
            this.init();
        }
    }

    showLoader() {
        const contentContainer = document.querySelector(".templatesWindowContent");
        const loader = document.createElement("div");
        const loaderInner = document.createElement("div");

        loaderInner.classList.add("lminiLoaderInner");
        loader.classList.add("templateLoader");
        loader.classList.add("miniLoader");

        loader.appendChild(loaderInner);
        if (contentContainer) {
            contentContainer.appendChild(loader);
        }
    }

    hideLoader() {
        const loader = document.querySelector(".templateLoader");

        loader?.remove();
    }

    close() {
        const templatesWindow = document.querySelector(".templatesWindowOuter");
        if (templatesWindow) {
            this.removeInputListeners();
            this.removeTemplateListeners();
            templatesWindow.outerHTML = "";
        }
    }

    inputFocus = () => {
        canv.appInterface.inputFocused = 1;
    };

    inputBlur = () => {
        canv.appInterface.inputFocused = 0;
    };

    inputKeyPress = (e) => {
        if (e.key === "Escape" || e.key === "Enter") {
            e.target.blur();
        }
    };

    searchTemplatesByTitle = (text: string) => {
        if (!text || !this.loadedTemplates) return this.loadedTemplates || [];

        const searchWords = text.toLowerCase().split(/\s+/).filter(Boolean);

        return this.loadedTemplates.filter((template) => {
            if (!template.title) return false;
            const titleLower = template.title.toLowerCase();
            return searchWords.every((word) => titleLower.includes(word));
        });
    };

    inputSearch = (e) => {
        const searchText = e.target.value.trim();

        const searchResult = this.searchTemplatesByTitle(searchText);

        this.removeTemplateListeners();

        const contentContainer = document.querySelector(".templatesWindowContent");
        if (contentContainer) {
            contentContainer.innerHTML = "";

            for (let i = 0; i < searchResult.length; i++) {
                contentContainer.innerHTML += this.applyItemTemplate(searchResult[i]);
            }

            this.initTemplateListeners();
        }
    };

    init() {
        this.initTemplateListeners();
        this.initInputListeners();
    }

    initTemplateListeners = () => {
        const items = document.querySelectorAll(".templatesWindowContentItem");
        for (let i = 0; i < items.length; i++) {
            items[i].addEventListener("mousedown", this.itemClick, false);
        }
    };

    removeTemplateListeners = () => {
        const items = document.querySelectorAll(".templatesWindowContentItem");
        for (let i = 0; i < items.length; i++) {
            items[i].removeEventListener("mousedown", this.itemClick, false);
        }
    };

    initInputListeners = () => {
        const searchInput = document.querySelector(".templatesSearchFieldInput");
        if (searchInput) {
            searchInput.addEventListener("focus", this.inputFocus, false);
            searchInput.addEventListener("blur", this.inputBlur, false);
            searchInput.addEventListener("input", this._inputSearchHandler, false);
            searchInput.addEventListener("keydown", this.inputKeyPress, false);
        }
    };

    removeInputListeners = () => {
        const searchInput = document.querySelector(".templatesSearchFieldInput");
        if (searchInput) {
            searchInput.removeEventListener("focus", this.inputFocus, false);
            searchInput.removeEventListener("blur", this.inputBlur, false);
            searchInput.removeEventListener("input", this._inputSearchHandler, false);
            searchInput.removeEventListener("keydown", this.inputKeyPress, false);
        }
    };

    async getContentData(): Promise<ApiGetTemplateListResponse> {
        if (this.loadedTemplates !== null) {
            return this.loadedTemplates;
        }

        const content = await ApiGetTemplateList();
        if (content) {
            this.loadedTemplates = content;
            return content;
        }
        return [];
    }

    // templates

    getWindow() {
        return `
            <div class="templatesWindowOuter">
                <div class="templatesWindow">
                    <div class="templatesWindowTitle">Классные шаблоны для ваших активностей</div>
                    <div class="templatesSearchBlock">
                        <div class="templatesSearchField">
                            <input type="text" class="templatesSearchFieldInput" placeholder="Название шаблона">
                        </div>
                    </div>
                    <div class="templatesWindowContent scrollable">

                    </div>
                </div>
            </div>
        `;
    }

    applyItemTemplate(data: LoadedTemplate) {
        return `
            <div class="templatesWindowContentItem" templateName="${data.templateName}">
                <div class="templatesWindowContentItemTitle">${data.title}</div>
                <div class="templatesWindowContentItemView">
                    <img src="${data.view}">
                </div>
            </div>
        `;
    }

    itemClick = async (e: Event) => {
        const elem = getTarget(e, "templatesWindowContentItem");
        const templateName: string = elem.getAttribute("templateName");

        let result: Response;
        try {
            result = await fetch(`${canv.host}/api/v2/template/get?templateName=${templateName}`, {
                method: "GET",
            });

            if (!result.ok) {
                logException(new Error("HTTP /api/v2/template/get fail"));
                return;
            }
        } catch (err: any) {
            logException(err);
            return;
        }

        const jsonRes = await result.json();
        /*
            В шаблонах не должно быть стрелок и mind map. Чтобы поддержать надо сделать как при вставке paste
            // mind maps могут быть
        */

        let newGroups: Group[];
        try {
            newGroups = (await canv.canvasController.loadFlatEnitites(
                jsonRes.data,
                true,
            )) as Group[];
        } catch (err: any) {
            logException(err);
            return;
        }

        canv.eventManager.openRecord();

        const oldProps = canv.canvasController.getSelectedProps(canv.canvasController.items);
        const templateProps = canv.canvasController.getSelectedProps(newGroups);

        const rightGap = canv.actionManager.getConstant(500);
        for (let i = 0; i < newGroups.length; i++) {
            newGroups[i].translateGroup(
                oldProps["startX"] + oldProps["width"] - templateProps["startX"] + rightGap,
                oldProps["startY"] - templateProps["startY"],
            );
            canv.eventManager.createBuildEvent("create", newGroups[i]);
        }

        selectionManager.setSelected(newGroups);
        canv.zoomToSelected();

        canv.eventManager.closeRecord();
        canv.redraw();
    };

    _inputSearchHandler = debounce(this.inputSearch, 500);

    loading = false;
    loadedTemplates: ApiGetTemplateListResponse | null = null;
}
